import { Component, computed, inject, signal, ChangeDetectionStrategy } from '@angular/core';
import { takeUntilDestroyed, toObservable } from '@angular/core/rxjs-interop';
import { filter, switchMap } from 'rxjs';
import { Router } from '@angular/router';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { ToastrService } from 'ngx-toastr';
import { SvgComponent } from '@components/svg.component';
import { AppRoutes } from '@helpers/app-routes';
import { OrderPaymentStatus, OrderStatusFilter } from '@models/order/order.model';
import { SessionOrderModel } from '@models/session-order/session-order.model';
import { SessionModel } from '@models/session/session.model';
import { SessionOrderService } from '@services/session-order.service';
import { SessionService } from '@services/session.service';
import { DatePipe } from '@angular/common';
import { OrderListHeaderComponent } from './order-list-header.component';
import { FormatPricePipe } from '@pipes/format-price.pipe';
import { LoaderComponent } from '@components/app-loader/app-loader.component';

@Component({
  selector: 'app-order-list-page',
  imports: [
    TranslateModule,
    SvgComponent,
    LoaderComponent,
    DatePipe,
    OrderListHeaderComponent,
    FormatPricePipe,
  ],
  templateUrl: './order-list-page.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  host: { class: 'block h-full' },
})
export class OrderListPageComponent {
  private readonly router = inject(Router);
  private readonly sessionService = inject(SessionService);
  private readonly sessionOrderService = inject(SessionOrderService);
  private readonly toastr = inject(ToastrService);
  private readonly translate = inject(TranslateService);

  protected readonly orders = signal<SessionOrderModel[]>([]);
  protected readonly isLoading = signal(true);
  protected readonly activeFilter = signal<OrderStatusFilter>('all');
  protected readonly searchQuery = signal('');

  protected readonly filteredSessionOrders = computed(() => {
    const activeFilter = this.activeFilter();
    const query = this.searchQuery().toLowerCase().trim();
    return this.orders().filter((item) => {
      const matchesFilter = activeFilter === 'all' || item.order.status === activeFilter;
      const matchesSearch =
        !query ||
        item.user.firstname?.toLowerCase().includes(query) ||
        item.user.lastname?.toLowerCase().includes(query) ||
        item.user.badge_number?.includes(query);
      return matchesFilter && matchesSearch;
    });
  });

  protected readonly filterCounts = computed(() => {
    const orders = this.orders();
    const counts: Partial<Record<OrderStatusFilter, number>> = { all: orders.length };
    for (const order of orders) {
      counts[order.order.status] = (counts[order.order.status] ?? 0) + 1;
    }
    return counts;
  });

  protected readonly totalCollectedFormatted = computed(() => {
    return this.orders().reduce((sum, item) => sum + item.order.price, 0);
  });

  constructor() {
    toObservable(this.sessionService.lastSession)
      .pipe(
        filter((session): session is SessionModel => session !== null),
        switchMap((session) => this.sessionOrderService.getSessionOrders(session.id)),
        takeUntilDestroyed(),
      )
      .subscribe({
        next: (orders) => {
          this.isLoading.set(false);
          this.orders.set(
            [...orders].sort((a, b) => this.#getSortTimestamp(b) - this.#getSortTimestamp(a)),
          );
        },
        error: () => {
          this.isLoading.set(false);
          this.toastr.error(
            this.translate.instant('order_list.errors.load_error_message'),
            this.translate.instant('order_list.errors.load_error_title'),
          );
        },
      });
  }

  #getSortTimestamp(sessionOrder: SessionOrderModel): number {
    const date = sessionOrder.forced_at ?? sessionOrder.validated_at ?? sessionOrder.created_at;
    return new Date(date).getTime();
  }

  protected getStatusClass(status: OrderPaymentStatus): string {
    const classes: Record<OrderPaymentStatus, string> = {
      valid: 'bg-vert-1',
      waiting: 'bg-orange-1',
      forced: 'bg-rose-1',
    };
    return classes[status];
  }

  protected getPaymentMode(payments: Record<string, number> | null): string {
    if (!payments) return '—';
    return Object.keys(payments)[0] ?? '—';
  }

  protected goToOrderDetail(sessionOrderId: number): void {
    this.router.navigate([AppRoutes.orderDetail, sessionOrderId]);
  }
}
